import React, { useState } from 'react';
import { formatTime, formatShiftTypes, isPredefinedSchedule } from '../../utils/scheduleUtils';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ScheduleTable = React.forwardRef(({ schedules, onEdit, onDelete, isDisabled }, ref) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState({});

  if (!schedules || schedules.length === 0) return null;

  const formatHoursToTimeString = (value) => {
    if (value === null || value === undefined) return '';
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const translateScheduleType = (type) => {
    switch (type) {
      case 'W': return t("form.schedule.table.type.work");
      case 'Off': return t("form.schedule.table.type.off");
      case 'Vacation': return t("form.schedule.table.type.vacation");
      case 'Holiday': return t("form.schedule.table.type.holiday");
      case 'Absence': return t("form.schedule.table.type.absence");
      case 'Comp': return t("form.schedule.table.type.comp");
      default: return type;
    }
  };

  const translateShiftType = (type) => {
    switch (type) {
      case 'M': return t("form.schedule.table.shift.morning");
      case 'T': return t("form.schedule.table.shift.afternoon");
      case 'N': return t("form.schedule.table.shift.night");
      case 'Off': return t("form.schedule.table.shift.off");
      case 'Vac': return t("form.schedule.table.shift.vacation");
      case 'Ho': return t("form.schedule.table.shift.holiday");
      case 'Abs': return t("form.schedule.table.shift.absence");
      case 'Comp': return t("form.schedule.table.shift.comp");
      default: return type;
    }
  };

  const toggleExpand = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const showMealBreaks = schedules.some(s => s.mealBreaks?.some(b => b.enabled));

  return (
    <div ref={ref} className="mt-8">
      <h3 className="text-lg font-medium mb-4">{t("form.schedule.table.title")}</h3>
      <div className="hidden md:block border rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-52 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("form.schedule.table.name")}
              </th>
              <th className="w-32 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("form.schedule.table.type")}
              </th>
              <th className="w-24 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("form.schedule.table.start")}
              </th>
              <th className="w-24 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("form.schedule.table.end")}
              </th>
              {showMealBreaks && (
                <>
                  <th className="w-24 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("form.schedule.table.mealStart")}
                  </th>
                  <th className="w-24 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("form.schedule.table.mealEnd")}
                  </th>
                </>
              )}
              <th className="w-28 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("form.schedule.table.weight")}
              </th>
              <th className="w-28 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("form.schedule.table.shiftType")}
              </th>
              <th className="w-48 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("form.schedule.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {schedules.map((schedule, index) => {
              const enabledMealBreak = schedule.mealBreaks?.find(b => b.enabled);
              const isPredefined = isPredefinedSchedule(schedule.code);
              const scheduleTypeValue = schedule.schedule_type || schedule.shiftTypes?.find(s => s.active)?.type || '';
              const shiftTypeValues = formatShiftTypes(schedule.shiftTypes || []);
              
              return (
                <tr key={index} className={`hover:bg-gray-50 ${isPredefined ? 'bg-gray-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {schedule.legend}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {translateScheduleType(scheduleTypeValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {schedule.startTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {schedule.endTime}
                  </td>
                  {showMealBreaks && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {enabledMealBreak ? enabledMealBreak.startTime : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {enabledMealBreak ? enabledMealBreak.endTime : '-'}
                      </td>
                    </>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {formatHoursToTimeString(schedule.weightTime)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {translateShiftType(shiftTypeValues)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex justify-center gap-3">
                      {!isPredefined && (
                        <>
                          <button
                            variant="outline"
                            size="sm"
                            onClick={() => onEdit(index, schedule)}
                            disabled={isDisabled}
                            className={`px-3 py-1 rounded-md transition-colors
                              ${
                                isDisabled 
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                          >
                            {t("form.schedule.table.edit")}
                          </button>
                          <button
                            variant="outline"
                            size="sm"
                            disabled={isDisabled}
                            onClick={() => {
                              if (window.confirm(t("form.schedule.table.confirmDelete"))) {
                                onDelete(index);
                              }
                            }}
                            className={`px-3 py-1 rounded-md transition-colors
                              ${
                                isDisabled 
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-red-100 text-red-600 hover:bg-red-200'
                              }`}
                          >
                            {t("form.schedule.table.delete")}
                          </button>
                        </>
                      )}
                      {isPredefined && (
                        <span className="text-gray-500 italic">{t("form.schedule.table.predefined")}</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden">
        <div className="space-y-4">
          {schedules.map((schedule, index) => {
            const enabledMealBreak = schedule.mealBreaks?.find(b => b.enabled);
            const isPredefined = isPredefinedSchedule(schedule.code);
            const scheduleTypeValue = schedule.schedule_type || schedule.shiftTypes?.find(s => s.active)?.type || '';
            const shiftTypeValues = formatShiftTypes(schedule.shiftTypes || []);
            const isExpanded = expandedItems[index];
            
            return (
              <div 
                key={index} 
                className={`border rounded-lg overflow-hidden ${isPredefined ? 'bg-gray-50' : 'bg-white'}`}
              >
                <div 
                  className="p-4 flex justify-between items-center cursor-pointer"
                  onClick={() => toggleExpand(index)}
                >
                  <div className="font-medium text-gray-900">{schedule.legend}</div>
                  <div className="text-gray-500">
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-y-2 text-sm">
                      <div className="text-gray-500">{t("form.schedule.table.type")}</div>
                      <div className="text-gray-900">{translateScheduleType(scheduleTypeValue)}</div>
                      
                      <div className="text-gray-500">{t("form.schedule.table.start")}</div>
                      <div className="text-gray-900">{schedule.startTime}</div>
                      
                      <div className="text-gray-500">{t("form.schedule.table.end")}</div>
                      <div className="text-gray-900">{schedule.endTime}</div>
                      
                      {enabledMealBreak && (
                        <>
                          <div className="text-gray-500">{t("form.schedule.table.mealStart")}</div>
                          <div className="text-gray-900">{enabledMealBreak.startTime}</div>
                          
                          <div className="text-gray-500">{t("form.schedule.table.mealEnd")}</div>
                          <div className="text-gray-900">{enabledMealBreak.endTime}</div>
                        </>
                      )}
                      
                      <div className="text-gray-500">{t("form.schedule.table.weight")}</div>
                      <div className="text-gray-900">{formatHoursToTimeString(schedule.weightTime)}</div>
                      
                      <div className="text-gray-500">{t("form.schedule.table.shiftType")}</div>
                      <div className="text-gray-900">{translateShiftType(shiftTypeValues)}</div>
                    </div>
                    
                    {!isPredefined && (
                      <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => onEdit(index, schedule)}
                          disabled={isDisabled}
                          className={`flex-1 px-3 py-2 rounded-md text-center transition-colors
                            ${
                              isDisabled 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                            }`}
                        >
                          {t("form.schedule.table.edit")}
                        </button>
                        <button
                          disabled={isDisabled}
                          onClick={() => {
                            if (window.confirm(t("form.schedule.table.confirmDelete"))) {
                              onDelete(index);
                            }
                          }}
                          className={`flex-1 px-3 py-2 rounded-md text-center transition-colors
                            ${
                              isDisabled 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-red-100 text-red-600 hover:bg-red-200'
                            }`}
                        >
                          {t("form.schedule.table.delete")}
                        </button>
                      </div>
                    )}
                    
                    {isPredefined && (
                      <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                        <span className="text-gray-500 italic">{t("form.schedule.table.predefined")}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default ScheduleTable;